import { action, makeObservable, observable, runInAction } from "mobx";
import CatalogProductsAPI from "../api/CatalogProductsAPI";
import {
  CreateProductInput,
  ProductDetailApi,
  ProductListApi,
  ProductListFilters,
  ProductPodApi,
  ProductVariantImageApi,
  UpdateCategoriesInput,
  UpdateImagesInput,
  UpdatePodInput,
  UpdatePodVariantsInput,
  UpdatePrintingTypesInput,
  UpdateProductInput,
  UpdateSeoInput,
  UpdateSourcingInput,
  UpdateVariantImagesInput,
  UpdateVariantsInput,
  UpdateVendorsInput,
} from "../../../models/CatalogProducts/types";
import { ProductActivityLog } from "../../../models/CatalogProducts/ProductActivityLog";
import { PurchaseRequestProductInsights } from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";

export class CatalogProductStore {
  catalogProductsAPI: CatalogProductsAPI;
  products: ProductListApi[] = [];
  productDetail: ProductDetailApi | null = null;
  productPod: ProductPodApi | null = null;
  purchaseRequestInsights: PurchaseRequestProductInsights[] = [];
  limit = 20;
  total_pages = 0;
  total_count = 0;
  error: string | null = null;
  activityLogs: ProductActivityLog[] = [];
  activityLogsPageNo = 1;
  activityLogsLimit = 20;
  activityLogsTotalCount = 0;
  activityLogsTotalPages = 0;
  activityLogsLoading = false;

  constructor() {
    this.catalogProductsAPI = new CatalogProductsAPI();
    makeObservable(this, {
      products: observable,
      productDetail: observable,
      productPod: observable,
      purchaseRequestInsights: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      error: observable,
      clearProductDetail: action,
      clearProductPod: action,
      patchListItem: action,
      listProducts: action,
      getProductById: action,
      createProduct: action,
      updateProduct: action,
      updateSourcing: action,
      updateSeo: action,
      updateImages: action,
      updateVariants: action,
      fetchVariants: action,
      updateVariantImages: action,
      updateCategories: action,
      updateVendors: action,
      updatePrintingTypes: action,
      toggleDisplay: action,
      toggleActive: action,
      getPod: action,
      updatePod: action,
      updatePodVariants: action,
      fetchPurchaseRequestInsights: action,
      activityLogs: observable,
      activityLogsPageNo: observable,
      activityLogsLimit: observable,
      activityLogsTotalCount: observable,
      activityLogsTotalPages: observable,
      activityLogsLoading: observable,
      getActivityLogs: action,
      resetActivityLogs: action,
    });
  }

  clearProductDetail = () => {
    this.productDetail = null;
    this.productPod = null;
    this.purchaseRequestInsights = [];
  };

  clearProductPod = () => {
    this.productPod = null;
  };

  patchListItem = (id: number, patch: Partial<ProductListApi>) => {
    const idx = this.products.findIndex((p) => p.id === id);
    if (idx >= 0) {
      this.products[idx] = { ...this.products[idx], ...patch };
    }
  };

  listProducts = async (params: ProductListFilters) => {
    try {
      const response = await this.catalogProductsAPI.listProducts(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.products = response.list;
      this.error = null;
      return response;
    } catch (error: any) {
      this.error =
        error?.response?.data?.message ||
        "Something went wrong loading products.";
      throw error;
    }
  };

  getProductById = async (id: number) => {
    try {
      const detail = await this.catalogProductsAPI.getProductById(id);
      this.productDetail = detail;
      return detail;
    } catch (error) {
      throw error;
    }
  };

  createProduct = async (body: CreateProductInput) => {
    try {
      const created = await this.catalogProductsAPI.createProduct(body);
      this.productDetail = created;
      return created;
    } catch (error) {
      throw error;
    }
  };

  updateProduct = async (id: number, body: UpdateProductInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateProduct(id, body);
      this.productDetail = updated;
      this.patchListItem(id, updated);
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updateSourcing = async (id: number, body: UpdateSourcingInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateSourcing(id, body);
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updateSeo = async (id: number, body: UpdateSeoInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateSeo(id, body);
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updateImages = async (id: number, body: UpdateImagesInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateImages(id, body);
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updateVariants = async (id: number, body: UpdateVariantsInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateVariants(id, body);
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  fetchVariants = async (productId: number) => {
    try {
      const variants =
        await this.catalogProductsAPI.fetchVariants(productId);
      if (this.productDetail?.id === productId) {
        this.productDetail = {
          ...this.productDetail,
          variants,
        };
      }
      return variants;
    } catch (error) {
      throw error;
    }
  };

  updateVariantImages = async (
    productId: number,
    variantId: number,
    body: UpdateVariantImagesInput,
  ) => {
    try {
      const result = await this.catalogProductsAPI.updateVariantImages(
        productId,
        variantId,
        body,
      );

      if (result.detail) {
        this.productDetail = result.detail;
        return result.detail;
      }

      if (this.productDetail?.id === productId && result.variantImages) {
        const images: ProductVariantImageApi[] = result.variantImages.filter(
          (img) => img.active,
        );
        this.productDetail = {
          ...this.productDetail,
          variants: this.productDetail.variants.map((v) =>
            v.id === variantId ? { ...v, images } : v,
          ),
        };
        return this.productDetail;
      }

      // Optimistic local merge when API returns empty/unknown shape
      if (this.productDetail?.id === productId) {
        const images: ProductVariantImageApi[] = body.images
          .filter((img) => (img.active ?? 1) === 1)
          .map((img, idx) => ({
            id: img.id ?? idx + 1,
            image_url: img.image_url,
            active: true,
          }));
        this.productDetail = {
          ...this.productDetail,
          variants: this.productDetail.variants.map((v) =>
            v.id === variantId ? { ...v, images } : v,
          ),
        };
      }

      return this.productDetail;
    } catch (error) {
      throw error;
    }
  };

  updateCategories = async (id: number, body: UpdateCategoriesInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateCategories(id, body);
      this.productDetail = updated;
      this.patchListItem(id, {
        category_id: body.category_id,
      });
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updateVendors = async (id: number, body: UpdateVendorsInput) => {
    try {
      const updated = await this.catalogProductsAPI.updateVendors(id, body);
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  updatePrintingTypes = async (
    id: number,
    body: UpdatePrintingTypesInput,
  ) => {
    try {
      const updated = await this.catalogProductsAPI.updatePrintingTypes(
        id,
        body,
      );
      this.productDetail = updated;
      return updated;
    } catch (error) {
      throw error;
    }
  };

  toggleDisplay = async (id: number) => {
    try {
      const result = await this.catalogProductsAPI.toggleDisplay(id);
      this.patchListItem(id, { display: result.display });
      if (this.productDetail?.id === id) {
        this.productDetail = {
          ...this.productDetail,
          display: result.display,
        };
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  toggleActive = async (id: number) => {
    try {
      const result = await this.catalogProductsAPI.toggleActive(id);
      this.patchListItem(id, { active: result.active });
      if (this.productDetail?.id === id) {
        this.productDetail = {
          ...this.productDetail,
          active: result.active,
        };
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  getPod = async (id: number) => {
    try {
      const pod = await this.catalogProductsAPI.getPod(id);
      this.productPod = pod;
      return pod;
    } catch (error) {
      this.productPod = null;
      throw error;
    }
  };

  updatePod = async (id: number, body: UpdatePodInput) => {
    try {
      const pod = await this.catalogProductsAPI.updatePod(id, body);
      this.productPod = pod;
      return pod;
    } catch (error) {
      throw error;
    }
  };

  updatePodVariants = async (id: number, body: UpdatePodVariantsInput) => {
    try {
      const pod = await this.catalogProductsAPI.updatePodVariants(id, body);
      this.productPod = pod;
      return pod;
    } catch (error) {
      throw error;
    }
  };

  getActivityLogs = async (
    id: number,
    pageNo: number = 1,
    limit: number = this.activityLogsLimit,
  ) => {
    this.activityLogsLoading = true;
    try {
      const response = await this.catalogProductsAPI.getActivityLogs(id, {
        page_no: pageNo,
        limit,
      });
      runInAction(() => {
        this.activityLogs = response.list || [];
        this.activityLogsPageNo = response.page_no;
        this.activityLogsLimit = response.limit;
        this.activityLogsTotalCount = response.total_count;
        this.activityLogsTotalPages = response.total_pages;
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.activityLogsLoading = false;
      });
    }
  };

  resetActivityLogs = () => {
    this.activityLogs = [];
    this.activityLogsPageNo = 1;
    this.activityLogsLimit = 20;
    this.activityLogsTotalCount = 0;
    this.activityLogsTotalPages = 0;
    this.activityLogsLoading = false;
  };

  fetchPurchaseRequestInsights = async (productId: number) => {
    try {
      const response =
        await this.catalogProductsAPI.getPurchaseRequestInsights(productId);
      runInAction(() => {
        this.purchaseRequestInsights = Array.isArray(response)
          ? (response as PurchaseRequestProductInsights[])
          : [];
      });
      return this.purchaseRequestInsights;
    } catch (error) {
      throw error;
    }
  };
}
